import { useEffect, useRef, useState } from "react";
import { checkSignupEmailAvailableRemote } from "@tennis-matches/services/governance";

function __isAuthEmailReadyForCheck(email) {
  const e = String(email || "").trim().toLowerCase();
  if (e.length < 6) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/**
 * Debounced Verfügbarkeitsprüfung (auth.users) für Login/Registrierung.
 * @returns {{ status: 'idle'|'checking'|'free'|'taken'|'error'|'skipped'|'rate_limited', available: boolean|null, errorMessageKey?: string, backendUnavailable?: boolean }}
 */
export function useAuthEmailAvailabilityCheck(email, { enabled = true, debounceMs = 900 } = {}) {
  const [result, setResult] = useState({
    status: "idle",
    available: null,
    retryAfterSec: null,
    errorMessageKey: null,
    backendUnavailable: false,
  });
  const seqRef = useRef(0);
  const backendDownRef = useRef(false);

  useEffect(() => {
    backendDownRef.current = false;
  }, [email]);

  useEffect(() => {
    if (!enabled) {
      setResult({ status: "idle", available: null, errorMessageKey: null, backendUnavailable: false });
      return undefined;
    }

    if (!__isAuthEmailReadyForCheck(email)) {
      setResult({ status: "idle", available: null, errorMessageKey: null, backendUnavailable: false });
      return undefined;
    }

    if (backendDownRef.current) {
      setResult({
        status: "error",
        available: null,
        errorMessageKey: "auth.register.emailCheck.errorBackendUnavailable",
        backendUnavailable: true,
      });
      return undefined;
    }

    const seq = ++seqRef.current;
    setResult({ status: "checking", available: null, errorMessageKey: null, backendUnavailable: false });

    const timer = setTimeout(async () => {
      const res = await checkSignupEmailAvailableRemote(email);
      if (seq !== seqRef.current) return;

      if (res?.backendUnavailable) {
        backendDownRef.current = true;
      }

      if (res?.rateLimited) {
        setResult({
          status: "rate_limited",
          available: null,
          retryAfterSec: res.retryAfterSec || 20,
          errorMessageKey: res.errorMessageKey || "auth.register.emailCheck.rateLimited",
          backendUnavailable: false,
        });
        return;
      }
      if (!res?.ok) {
        setResult({
          status: "error",
          available: null,
          errorMessageKey: res.errorMessageKey || "auth.register.emailCheck.errorTechnical",
          backendUnavailable: res.backendUnavailable === true,
        });
        return;
      }
      if (res.skipped) {
        setResult({ status: "skipped", available: null, errorMessageKey: null, backendUnavailable: false });
        return;
      }
      setResult({
        status: res.available ? "free" : "taken",
        available: res.available === true,
        errorMessageKey: null,
        backendUnavailable: false,
      });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [email, enabled, debounceMs]);

  return result;
}
